(() => {
  'use strict';
  if (window.FireflyVariantSync) { window.FireflyVariantSync.init(); return; }

  const selector = '[data-ff-version-view]';
  const initialized = new WeakSet();
  const configs = new WeakMap();
  const path = value => { try { return new URL(value, location.origin).pathname.replace(/\/$/, ''); } catch { return ''; } };
  const roots = () => Array.from(document.querySelectorAll(selector));

  function config(root) {
    if (!configs.has(root)) {
      try {
        const variants = JSON.parse(root.querySelector('[data-ff-variants]').textContent);
        configs.set(root, Array.isArray(variants) ? variants : []);
      } catch { configs.set(root, []); }
    }
    return configs.get(root);
  }

  function owner(root) {
    let main = root.closest('[id^="MainProduct-"]');
    if (!main) {
      const summary = roots().find(candidate => candidate.dataset.ffVersionView === 'summary' &&
        candidate.dataset.productId === root.dataset.productId &&
        path(candidate.dataset.productUrl) === path(root.dataset.productUrl) &&
        candidate.closest('[id^="MainProduct-"]'));
      main = summary && summary.closest('[id^="MainProduct-"]');
    }
    if (!main) return null;
    const sectionId = main.dataset.section;
    const picker = main.querySelector('variant-selects, variant-radios');
    if (picker && path(picker.dataset.url) !== path(root.dataset.productUrl)) return null;
    const form = document.getElementById('product-form-' + sectionId);
    return {main, sectionId, picker, form};
  }

  function status(root, message) {
    const node = root.querySelector('[data-ff-version-status]');
    if (node) node.textContent = message;
  }

  function render(root, variantId, announce = false) {
    const variant = config(root).find(item => String(item.id) === String(variantId));
    root.dataset.selectedVariant = variant ? String(variant.id) : '';
    let firstEnabled;
    root.querySelectorAll('[data-ff-variant-tab]').forEach(tab => {
      const selected = !!variant && tab.dataset.ffVariantTab === String(variant.id);
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (!tab.disabled && !firstEnabled) firstEnabled = tab;
    });
    if ((!variant || !variant.available) && firstEnabled) firstEnabled.tabIndex = 0;
    root.querySelectorAll('[data-ff-version-panel]').forEach(panel => {
      const selected = !!variant && panel.dataset.ffVersionPanel === String(variant.id);
      panel.hidden = !selected;
      panel.classList.toggle('is-active', selected);
      panel.classList.toggle('is-on', selected);
    });
    root.querySelectorAll('[data-ff-variant-choice]').forEach(button => button.setAttribute('aria-pressed',String(!!variant && button.dataset.ffVariantChoice === String(variant.id))));
    if (!variant) status(root, 'Choose an available version in the product options.');
    else if (announce) status(root, variant.title + (variant.available ? ' selected.' : ' — sold out.'));
  }

  function sync(sectionId, variantId, announce = false) {
    roots().forEach(root => {
      const context = owner(root);
      if (context && context.sectionId === sectionId) render(root, variantId, announce);
    });
  }

  function currentId(context) {
    if (context.picker && context.picker.currentVariant) return context.picker.currentVariant.id;
    return context.form && context.form.querySelector('[name="id"]')?.value;
  }

  // Set every option first, then dispatch exactly one native change. Shrine owns
  // price rendering, availability, URL changes, and the add-to-cart variant ID.
  function select(root, variantId) {
    const variant = config(root).find(item => String(item.id) === String(variantId));
    const context = owner(root);
    if (!variant || !variant.available || !context || !context.picker ||
        typeof context.picker.onVariantChange !== 'function') {
      status(root, 'Choose an available version in the product options above.');
      return false;
    }
    const groups = Array.from(context.picker.querySelectorAll('.product-form__input'));
    if (groups.length !== variant.options.length) return false;
    const controls = groups.map((group, index) => {
      const value = String(variant.options[index]);
      const dropdown = group.querySelector('select');
      if (dropdown) return Array.from(dropdown.options).some(option => option.value === value) ? {control:dropdown, value} : null;
      const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(input => input.value === value);
      return radio ? {control:radio, value} : null;
    });
    if (controls.some(control => !control)) {
      status(root, 'Choose this version in the product options above.');
      return false;
    }
    controls.forEach(({control, value}) => {
      if (control.tagName === 'SELECT') control.value = value;
      else control.checked = true;
    });
    controls[controls.length - 1].control.dispatchEvent(new Event('change', {bubbles:true}));
    const actual = context.picker.currentVariant;
    sync(context.sectionId, actual ? actual.id : null, true);
    if (!actual || String(actual.id) !== String(variant.id)) {
      status(root, 'That version is unavailable. Please choose another version.');
      return false;
    }
    return true;
  }

  function init() {
    roots().forEach(root => {
      if (initialized.has(root)) return;
      initialized.add(root);
      config(root);
      root.addEventListener('click', event => {
        const tab = event.target.closest('[data-ff-variant-tab], [data-ff-variant-choice]');
        if (tab && root.contains(tab) && !tab.disabled) select(root, tab.dataset.ffVariantTab || tab.dataset.ffVariantChoice);
      });
      root.addEventListener('keydown', event => {
        const tab = event.target.closest('[data-ff-variant-tab]');
        if (!tab || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        const tabs = Array.from(root.querySelectorAll('[data-ff-variant-tab]')).filter(item => !item.disabled);
        if (!tabs.length) return;
        event.preventDefault();
        const index = tabs.indexOf(tab);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 :
          (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        tabs[next].focus();
        select(root, tabs[next].dataset.ffVariantTab);
      });
      const context = owner(root);
      if (context) render(root, currentId(context) || root.dataset.selectedVariant);
    });
  }

  // Both hooks below were verified against the live Shrine variant-selects.
  // Native form ID changes occur synchronously; the pubsub event confirms the
  // final variant after Shopify's rendered prices/availability have arrived.
  document.addEventListener('change', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const picker = target.closest('variant-selects, variant-radios');
    if (picker) { sync(picker.dataset.section, picker.currentVariant?.id, true); return; }
    if (target.getAttribute('name') !== 'id') return;
    const form = target.closest('form');
    const formId = form && form.getAttribute('id');
    if (!formId || !formId.startsWith('product-form-') || formId.startsWith('product-form-installment-')) return;
    sync(formId.slice('product-form-'.length), target.value, true);
  });

  let subscribed = false;
  function subscribeToTheme() {
    if (subscribed || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS !== 'object' || !PUB_SUB_EVENTS.variantChange) return;
    subscribed = true;
    subscribe(PUB_SUB_EVENTS.variantChange, event => {
      if (event?.data?.sectionId) sync(event.data.sectionId, event.data.variant?.id);
    });
  }

  window.FireflyVariantSync = {init};
  const start = () => { init(); subscribeToTheme(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
  if (window.customElements) customElements.whenDefined('variant-selects').then(start);
  document.addEventListener('shopify:section:load', start);
  window.addEventListener('pageshow', () => {
    init();
    roots().forEach(root => { const context = owner(root); if (context) render(root, currentId(context)); });
  });
})();
